import { RoleIndex } from "../../../../../models/role";
import { UserType } from "../../../../../models/userType";
import { compareTwoObjectArray } from "../../../../../utils/utils";
import {
  AllRecordsTrainingTypes,
  UserFormValues,
  isTrainingType,
} from "./userDialogModel";

// Four payload grammars, one per (mode, program) — the backend still exposes
// two resources. Pure, so the shapes diff against the old if-ladder by eye.

const clientIndexes = (clients: any) =>
  (clients || []).map((client: any) => client.client_index);

/** Identity fields every create payload carries. */
const identity = (values: UserFormValues, roleIndex: number) => ({
  email: values.email,
  role_index: roleIndex,
  first_name: values.first_name,
  last_name: values.last_name,
});

// ── create ───────────────────────────────────────────────────────────────────

/** POST /api/register — everything that is not a training user. */
export const buildCreateAnalyticsPayload = (values: UserFormValues) => {
  const roleIndex = values.user_role?.role_index;
  const base = identity(values, roleIndex);

  if (roleIndex === RoleIndex.ClientUser) {
    const clientTypeIndex = values.client_type?.client_type_index;
    return {
      ...base,
      client: values.client?.client_index,
      client_type: clientTypeIndex,
      client_title: values.client_title?.client_title_key,
      client_branch: values.client_branch?.value,
      client_department: values.client_department?.value,
      // Only a Sales_Rep user carries a rep; other types sent nothing at all.
      ...(clientTypeIndex === UserType.Index.SalesRep
        ? { sale_rep: values.sale_rep?.SALESMAN }
        : {}),
    };
  }

  if (roleIndex === RoleIndex.AccountAdmin) {
    return {
      ...base,
      client: clientIndexes(values.clients),
      client_title: values.client_title?.client_title_key,
    };
  }

  if (roleIndex === RoleIndex.Admin) {
    return { ...base, client: clientIndexes(values.clients) };
  }

  // Super: identity only, no client scope.
  return base;
};

/** POST /api/training_users — keeps training's scope rules in one place. */
export const buildCreateTrainingPayload = (values: UserFormValues) => {
  const clientTypeIndex = values.client_type?.client_type_index;
  const roleType = values.client_type?.client_type_name;

  return {
    ...identity(values, RoleIndex.ClientUser),
    client_user_type_index: clientTypeIndex,
    client: values.client?.client_index,
    role_type: roleType,
    user_domain:
      clientTypeIndex === UserType.Index.LWS_DEALER ? values.user_domain : null,
    user_school:
      clientTypeIndex === UserType.Index.LWS_SCHOOL
        ? values.user_school?.name ?? values.user_school
        : null,
    // LWS_ADMIN and LWS_SUPPORT scope to every record.
    user_admin: AllRecordsTrainingTypes.includes(clientTypeIndex) ? true : null,
  };
};

// ── change detection ─────────────────────────────────────────────────────────

export const nameChanged = (values: UserFormValues, editUserData: any) =>
  values.first_name !== editUserData?.first_name ||
  values.last_name !== editUserData?.last_name;

/** Mirrors handleEditUserChangeValidate, minus its setState side effects. */
export const profileChanged = (values: UserFormValues, editUserData: any) => {
  const roleIndex = values.user_role?.role_index;
  if (roleIndex !== editUserData?.role_index) return true;

  if (roleIndex === RoleIndex.ClientUser) {
    const sameCore =
      editUserData?.client_type_index === values.client_type?.client_type_index &&
      editUserData?.client_list?.[0]?.client_index === values.client?.client_index &&
      editUserData?.client_title === values.client_title?.client_title_key &&
      editUserData?.client_branch === values.client_branch?.value &&
      editUserData?.client_department === values.client_department?.value;

    if (editUserData?.client_type_index === UserType.Index.SalesRep) {
      // By name: rep options carry SALESMAN, not salemen_index.
      return !(sameCore && editUserData?.salemen_name === values.sale_rep?.SALESMAN);
    }
    return !sameCore;
  }

  if (roleIndex === RoleIndex.AccountAdmin) {
    const sameClients = compareTwoObjectArray(
      editUserData?.client_list || [],
      values.clients || [],
      "client_index"
    );
    return !(
      sameClients &&
      editUserData?.client_title === values.client_title?.client_title_key
    );
  }

  if (roleIndex === RoleIndex.Admin) {
    return !compareTwoObjectArray(
      editUserData?.client_list || [],
      values.clients || [],
      "client_index"
    );
  }

  return false;
};

/** Training scope lives in people_user, so it is diffed against that record. */
export const trainingScopeChanged = (
  values: UserFormValues,
  trainingRecord: any
) => {
  const clientTypeIndex = values.client_type?.client_type_index;

  if (clientTypeIndex === UserType.Index.LWS_SCHOOL) {
    const school = values.user_school?.name ?? values.user_school;
    return school !== trainingRecord?.r_operand;
  }
  if (clientTypeIndex === UserType.Index.LWS_DEALER) {
    return values.user_domain !== trainingRecord?.r_operand;
  }
  return false;
};

// ── edit ─────────────────────────────────────────────────────────────────────

/** PUT /api/update_user — the `update_type` plus current/new pairs grammar. */
export const buildEditAnalyticsPayload = (
  values: UserFormValues,
  editUserData: any,
  changes: { name: boolean; profile: boolean }
) => {
  const update_type: string[] = [];
  const user_index = editUserData?.client_user_index;
  let payload: any = {};

  if (changes.name) {
    update_type.push("name");
    payload = {
      ...payload,
      user_index,
      current_first_name: editUserData?.first_name,
      current_last_name: editUserData?.last_name,
      new_first_name: values.first_name,
      new_last_name: values.last_name,
    };
  }

  if (changes.profile) {
    update_type.push("role");
    const roleIndex = values.user_role?.role_index;
    const roleCore = {
      user_index,
      new_user_role_index: roleIndex,
      current_update_user_role_index: editUserData?.role_index,
      current_update_user_client_list: editUserData?.client_list,
    };

    if (roleIndex === RoleIndex.ClientUser) {
      const clientTypeIndex = values.client_type?.client_type_index;
      payload = {
        ...payload,
        ...roleCore,
        new_client_list: [values.client],
        new_client_type_index: clientTypeIndex,
        // Non-sales-rep types clear the rep rather than omitting it.
        new_sale_rep:
          clientTypeIndex === UserType.Index.SalesRep
            ? values.sale_rep?.SALESMAN
            : "",
        client_title: values.client_title?.client_title_key,
        current_update_client_title: editUserData?.client_title,
        client_branch: values.client_branch?.value,
        current_update_client_branch: editUserData?.client_branch,
        client_department: values.client_department?.value,
        current_update_client_department: editUserData?.client_department,
      };
    } else if (roleIndex === RoleIndex.AccountAdmin) {
      payload = {
        ...payload,
        ...roleCore,
        new_client_list: values.clients,
        client_title: values.client_title?.client_title_key,
        current_update_client_title: editUserData?.client_title,
      };
    } else if (roleIndex === RoleIndex.Admin) {
      payload = { ...payload, ...roleCore, new_client_list: values.clients };
    } else {
      payload = { ...payload, ...roleCore };
    }
  }

  return { ...payload, update_type };
};

/** PUT /api/training_users — its own update_type vocabulary. */
export const buildEditTrainingPayload = (
  values: UserFormValues,
  editUserData: any,
  trainingRecord: any,
  changes: { name: boolean; roleType: boolean; scope: boolean }
) => {
  const clientTypeIndex = values.client_type?.client_type_index;
  const payload: any = {
    user_index: editUserData?.client_user_index,
    update_type: [] as string[],
  };

  if (changes.name) {
    payload.update_type.push("name");
    payload.current_first_name = editUserData?.first_name;
    payload.current_last_name = editUserData?.last_name;
    payload.new_first_name = values.first_name;
    payload.new_last_name = values.last_name;
  }

  if (changes.roleType) {
    payload.update_type.push("role_type");
    payload.new_user_role_type = values.client_type?.client_type_name;
    payload.new_user_is_admin = AllRecordsTrainingTypes.includes(clientTypeIndex)
      ? UserType.Name.LWS_ADMIN
      : null;
    payload.new_client_user_type_index = clientTypeIndex;
  }

  // A role-type change rewrites scope too: the predicate belongs to the old role.
  if (changes.roleType || changes.scope) {
    if (clientTypeIndex === UserType.Index.LWS_SCHOOL) {
      payload.update_type.push("school");
      payload.new_user_school = values.user_school?.name ?? values.user_school;
    }
    if (clientTypeIndex === UserType.Index.LWS_DEALER) {
      payload.update_type.push("dealer");
      payload.new_user_domain = values.user_domain;
    }
  }

  return payload;
};

/** Which resource a given form state belongs to. */
export const isTrainingUser = (values: UserFormValues) =>
  values.user_role?.role_index === RoleIndex.ClientUser &&
  isTrainingType(values.client_type?.client_type_index);

/** Same question, for a row coming back from /api/users. */
export const isTrainingRow = (editUserData: any) =>
  editUserData?.role_index === RoleIndex.ClientUser &&
  isTrainingType(editUserData?.client_type_index);
